import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import "./ImageLightbox.css";

const ImageLightbox = ({ images, initialIndex = 0, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStartX, setTouchStartX] = useState(null);
  const [direction, setDirection] = useState(0);

  // Sync index when lightbox opens with a new initialIndex
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setDirection(0);
    }
  }, [isOpen, initialIndex]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const totalImages = images?.length || 0;

  const goToNext = useCallback(() => {
    if (totalImages <= 1) return;
    setDirection(1);
    setCurrentIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  }, [totalImages]);

  const goToPrev = useCallback(() => {
    if (totalImages <= 1) return;
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  }, [totalImages]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          goToPrev();
          break;
        case "ArrowRight":
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, goToNext, goToPrev]);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartX - touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
    setTouchStartX(null);
  };

  // Click on overlay (outside image) to close
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("lightbox-overlay") || 
        e.target.classList.contains("lightbox-image-container")) {
      onClose();
    }
  };

  if (!images || images.length === 0) return null;

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={handleOverlayClick}
        >
          {/* Close button */}
          <button
            className="lightbox-close"
            onClick={onClose}
            aria-label="Fermer"
            type="button"
          >
            <FaTimes />
          </button>

          {/* Image counter */}
          <div className="lightbox-counter">
            {currentIndex + 1} / {totalImages}
          </div>

          {/* Main image area */}
          <div
            className="lightbox-image-container"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                alt={`Image ${currentIndex + 1}`}
                className="lightbox-image"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeInOut" }}
                draggable={false}
              />
            </AnimatePresence>
          </div>

          {/* Navigation arrows */}
          {totalImages > 1 && (
            <>
              <button
                className="lightbox-nav lightbox-nav-prev"
                onClick={goToPrev}
                aria-label="Image précédente"
                type="button"
              >
                <FaChevronLeft />
              </button>
              <button
                className="lightbox-nav lightbox-nav-next"
                onClick={goToNext}
                aria-label="Image suivante"
                type="button"
              >
                <FaChevronRight />
              </button>
            </>
          )}

          {/* Dots indicator */}
          {totalImages > 1 && (
            <div className="lightbox-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`lightbox-dot ${index === currentIndex ? "active" : ""}`}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  aria-label={`Image ${index + 1}`}
                  type="button"
                />
              ))}
            </div>
          )}

          {/* Thumbnails strip */}
          {totalImages > 1 && (
            <div className="lightbox-thumbnails">
              {images.map((img, index) => (
                <button
                  key={index}
                  className={`lightbox-thumbnail ${index === currentIndex ? "active" : ""}`}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  type="button"
                >
                  <img src={img} alt={`Miniature ${index + 1}`} draggable={false} />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageLightbox;
